import { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AuthImage from "@/components/AuthImage";
import { Plus, ImagePlus } from "lucide-react";

export default function SuggestPlanDialog({ categories, isAdmin, onCreated, trigger, editPlan = null }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoPath, setPhotoPath] = useState(editPlan?.photo_path || null);
  const fileRef = useRef(null);
  const [form, setForm] = useState(editPlan ? {
    title: editPlan.title || "", category: editPlan.category || categories[0] || "Wellness",
    date: editPlan.date || "", time: editPlan.time || "", location: editPlan.location || "",
    seats: editPlan.seats ?? 10, value: editPlan.value ?? "", description: editPlan.description || "",
    recurrence: "pontual", occurrences: 1,
  } : {
    title: "", category: categories[0] || "Wellness", date: "", time: "", location: "",
    seats: 10, value: "", description: "", recurrence: "pontual", occurrences: 4,
  });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(data.path);
      toast.success("Foto associada ao plano.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        seats: parseInt(form.seats) || 0,
        value: form.value === "" ? null : parseFloat(form.value),
        occurrences: parseInt(form.occurrences) || 1,
        photo_path: photoPath,
      };
      if (editPlan) {
        await api.put(`/admin/plans/${editPlan.id}`, payload);
        toast.success("Plano atualizado!");
        setOpen(false);
      } else {
        const { data } = await api.post("/plans", payload);
        toast.success(isAdmin
          ? `Plano criado${data.created.length > 1 ? ` (${data.created.length} ocorrências)` : ""}!`
          : "Plano enviado ✨ Vamos dar uma vista de olhos e avisamos-te assim que estiver aprovado.");
        setOpen(false);
        setForm({ ...form, title: "", date: "", location: "", value: "", description: "" });
        setPhotoPath(null);
      }
      onCreated && onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <button data-testid="open-suggest-plan-btn" className="inline-flex items-center gap-2 bg-[#48020C] hover:bg-[#26130E] text-[#FCFCFA] rounded-full px-6 py-3 transition-colors">
            <Plus className="w-4 h-4" /> {isAdmin ? "Criar plano" : "Sugerir plano"}
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="bg-[#FCFCFA] border-[#E3DED2] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-[#26130E]">{editPlan ? "Editar plano" : isAdmin ? "Criar novo plano" : "Sugerir um plano"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} data-testid="suggest-plan-form" className="space-y-5 mt-2">
          {/* Photo */}
          <div className="space-y-1.5">
            <Label className="sc-label">Foto do plano (opcional)</Label>
            <button type="button" data-testid="plan-photo-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="w-full rounded-xl border border-dashed border-[#48020C] bg-[#F4F1EA] hover:bg-[#ECE7DC] transition-colors overflow-hidden">
              {photoPath ? (
                <AuthImage path={photoPath} className="w-full h-40 object-cover" alt="Foto do plano" />
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-[#6E5F58]">
                  <ImagePlus className="w-6 h-6" />
                  <span className="text-sm mt-2">{uploading ? "A carregar…" : "Adicionar fotografia"}</span>
                </div>
              )}
            </button>
            <input ref={fileRef} data-testid="plan-photo-input" type="file" accept="image/*" onChange={upload} className="hidden" />
          </div>

          <F label="Título do plano"><Input data-testid="plan-title" required value={form.title} onChange={set("title")} className="sc-input2" placeholder="Ex: Jantar às cegas na Ribeira" /></F>
          <F label="Categoria">
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="plan-category" className="sc-input2"><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <div className="grid grid-cols-2 gap-4">
            <F label="Data"><Input data-testid="plan-date" type="date" required value={form.date} onChange={set("date")} className="sc-input2" /></F>
            <F label="Hora"><Input data-testid="plan-time" type="time" value={form.time} onChange={set("time")} className="sc-input2" /></F>
          </div>
          <F label="Local (Porto)"><Input data-testid="plan-location" required value={form.location} onChange={set("location")} className="sc-input2" placeholder="Ex: Foz do Douro" /></F>
          <div className="grid grid-cols-2 gap-4">
            <F label="Nº de lugares"><Input data-testid="plan-seats" type="number" min={1} required value={form.seats} onChange={set("seats")} className="sc-input2" /></F>
            <F label="Valor € (vazio = grátis)"><Input data-testid="plan-value" type="number" min={0} step="0.01" value={form.value} onChange={set("value")} className="sc-input2" placeholder="Gratuito" /></F>
          </div>
          <F label="Descrição"><Textarea data-testid="plan-description" value={form.description} onChange={set("description")} className="sc-input2 min-h-20" placeholder="Detalhes do plano…" /></F>
          {!editPlan && (
          <F label="Recorrência">
            <Select value={form.recurrence} onValueChange={(v) => setForm({ ...form, recurrence: v })}>
              <SelectTrigger data-testid="plan-recurrence" className="sc-input2"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pontual">Pontual (uma vez)</SelectItem>
                <SelectItem value="semanal">Semanal</SelectItem>
                <SelectItem value="mensal">Mensal</SelectItem>
              </SelectContent>
            </Select>
          </F>
          )}
          {!editPlan && isAdmin && form.recurrence !== "pontual" && (
            <F label="Quantas ocorrências independentes?">
              <Input data-testid="plan-occurrences" type="number" min={1} max={52} value={form.occurrences} onChange={set("occurrences")} className="sc-input2" />
            </F>
          )}
          <button type="submit" disabled={loading} data-testid="submit-plan-btn" className="w-full bg-[#48020C] hover:bg-[#26130E] text-[#F7EEC7] font-medium rounded-full py-3.5 transition-colors">
            {loading ? "A guardar…" : editPlan ? "Guardar alterações" : isAdmin ? "Criar plano" : "Enviar sugestão"}
          </button>
        </form>
        <style>{`.sc-input2{background:#F4F1EA;border-color:#E3DED2;border-radius:0.6rem;height:auto;padding:0.6rem 0.85rem;}`}</style>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="sc-label">{label}</Label>
      {children}
    </div>
  );
}
